import { ChangeDetectionStrategy, Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

import { EMPTY, catchError, finalize } from 'rxjs';
import { BenutzerverwaltungService } from '../../services/benutzerverwaltung.service';
import { Rolle } from '../../models/rolle.model';
import {
  RollenDialogComponent,
  RolleDialogData,
  RolleDialogResult,
  ModulOption,
} from '../../components/rollen-dialog/rollen-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-rollen-tab',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatSnackBarModule],
  templateUrl: './rollen-tab.component.html',
  styleUrl: './rollen-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RollenTabComponent {
  private readonly benutzerverwaltungService = inject(BenutzerverwaltungService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  protected readonly rollen = signal<Rolle[]>([]);
  protected readonly currentUserRolleIds = signal<string[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly skeletonItems = Array.from({ length: 6 });
  protected readonly moduleOptions = signal<ModulOption[]>([]);

  @Output() readonly rollenChanged = new EventEmitter<void>();

  constructor() {
    this.loadRollen(true);
  }

  protected createRolle(): void {
    this.loadModuleOptions(() => {
      let submittedRoleName = '';

      const dialogRef = this.dialog.open<
        RollenDialogComponent,
        RolleDialogData,
        boolean
      >(RollenDialogComponent, {
        width: '512px',
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100svh - 16px)',
        panelClass: 'role-dialog-panel',
        autoFocus: false,
        restoreFocus: false,
        data: {
          mode: 'create',
          moduleOptions: this.moduleOptions(),
          submitRequest: (result: RolleDialogResult) => {
            submittedRoleName = result.name;

            return this.benutzerverwaltungService.createRolle({
              name: result.name,
              istAdminRolle: result.istAdmin,
              module: result.module,
            });
          },
        },
      });

      dialogRef.afterClosed().subscribe((saved) => {
        if (!saved) {
          return;
        }

        this.loadRollen(true);
        this.rollenChanged.emit();

        this.snackBar.open(`Rolle "${submittedRoleName}" wurde gespeichert.`, '×', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar'],
        });
      });
    });
  }

  protected editRolle(rolle: Rolle): void {
    if (this.isCurrentUserRolle(rolle)) {
      return;
    }

    this.benutzerverwaltungService
      .acquireRolleLock(rolle.id)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 423) {
            return EMPTY;
          }

          this.snackBar.open(
            'Rolle konnte nicht zum Bearbeiten gesperrt werden.',
            '×',
            {
              duration: 6000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );

          return EMPTY;
        })
      )
      .subscribe(() => {
        this.loadModuleOptions(() => {
          let submittedRoleName = rolle.name;

          const dialogRef = this.dialog.open<
            RollenDialogComponent,
            RolleDialogData,
            boolean
          >(RollenDialogComponent, {
            width: '512px',
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100svh - 16px)',
            panelClass: 'role-dialog-panel',
            autoFocus: false,
            restoreFocus: false,
            data: {
              mode: 'edit',
              rolle,
              moduleOptions: this.moduleOptions(),
              submitRequest: (result: RolleDialogResult) => {
                submittedRoleName = result.name;

                return this.benutzerverwaltungService.updateRolle(rolle.id, {
                  name: result.name,
                  istAdminRolle: result.istAdmin,
                  module: result.module,
                });
              },
            },
          });

          dialogRef.afterClosed().subscribe((saved) => {
            this.releaseRolleLock(rolle.id);

            if (!saved) {
              return;
            }

            this.loadRollen(true);
            this.rollenChanged.emit();

            this.snackBar.open(`Rolle "${submittedRoleName}" wurde aktualisiert.`, '×', {
              duration: 4000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['success-snackbar'],
            });
          });
        });
      });
  }

  protected deleteRolle(rolle: Rolle): void {
    if (this.isCurrentUserRolle(rolle)) {
      return;
    }

    this.benutzerverwaltungService
      .acquireRolleLock(rolle.id)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 423) {
            const lockedBy =
              error.error?.blockedKeys?.[0]?.lockedByName ??
              'einem anderen Benutzer';

            this.snackBar.open(
              `Rolle ist aktuell von ${lockedBy} gesperrt und kann nicht gelöscht werden.`,
              '×',
              {
                duration: 6000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['error-snackbar'],
              }
            );

            return EMPTY;
          }

          this.snackBar.open(
            'Rolle konnte nicht zum Löschen gesperrt werden.',
            '×',
            {
              duration: 6000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );

          return EMPTY;
        })
      )
      .subscribe(() => {
        const dialogRef = this.dialog.open<
          ConfirmDialogComponent,
          ConfirmDialogData,
          boolean
        >(ConfirmDialogComponent, {
          width: '420px',
          maxWidth: 'calc(100vw - 32px)',
          panelClass: 'role-dialog-panel',
          autoFocus: false,
          restoreFocus: false,
          data: {
            title: 'Rolle löschen',
            message: `Möchtest du die Rolle "${rolle.name}" wirklich löschen?`,
            cancelText: 'Abbrechen',
            confirmText: 'Löschen',
          },
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
          if (!confirmed) {
            this.releaseRolleLock(rolle.id);
            return;
          }

          this.benutzerverwaltungService
            .deleteRolle(rolle.id)
            .pipe(
              finalize(() => {
                this.releaseRolleLock(rolle.id);
              })
            )
            .subscribe({
              next: () => {
                this.loadRollen(true);
                this.rollenChanged.emit();

                this.snackBar.open(`Rolle "${rolle.name}" wurde gelöscht.`, '×', {
                  duration: 4000,
                  horizontalPosition: 'right',
                  verticalPosition: 'top',
                  panelClass: ['success-snackbar'],
                });
              },
            });
        });
      });
  }

  private loadRollen(forceRefresh = false): void {
    this.isLoading.set(true);

    this.benutzerverwaltungService.getRollen(forceRefresh).subscribe({
      next: (data) => {
        this.rollen.set(data);
        this.updateCurrentUserRolleIds(data);
        this.isLoading.set(false);
      },
      error: () => {
        this.rollen.set([]);
        this.currentUserRolleIds.set([]);
        this.isLoading.set(false);
      },
    });
  }

  private loadModuleOptions(afterLoaded?: () => void): void {
    if (this.moduleOptions().length > 0) {
      afterLoaded?.();
      return;
    }

    this.benutzerverwaltungService.getModule().subscribe({
      next: (modules) => {
        this.moduleOptions.set(
          modules.map((module) => ({
            value: module,
            label: this.getModuleLabel(module),
          }))
        );

        afterLoaded?.();
      },
      error: () => {
        this.moduleOptions.set([]);
        afterLoaded?.();
      },
    });
  }

  private releaseRolleLock(rolleId: string): void {
    this.benutzerverwaltungService.releaseRolleLock(rolleId).subscribe({
      error: () => {
        // Lock may already be expired or released. No UI error needed.
      },
    });
  }

  private getModuleLabel(module: string): string {
    const labels: Record<string, string> = {
      Wareneingaenge: 'Wareneingänge',
    };

    return labels[module] ?? module;
  }

  protected isCurrentUserRolle(rolle: Rolle): boolean {
    return this.currentUserRolleIds().includes(rolle.id);
  }

  private updateCurrentUserRolleIds(rollen: Rolle[]): void {
    if (this.authService.isCopagoAdmin() || this.authService.isSuperAdmin()) {
      this.currentUserRolleIds.set([]);
      return;
    }

    const currentRoleNames = this.authService.getRoles();

    const currentRoleIds = rollen
      .filter((rolle) => currentRoleNames.includes(rolle.name))
      .map((rolle) => rolle.id);

    this.currentUserRolleIds.set(currentRoleIds);
  }
}